import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { CreateExperimentInput } from "@/types/experiment";
import { getEmptyMetadataColumns } from "@/utils/csvUtils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PlotMapSection, { PlotMapChangeData, PlotMapData } from "./PlotMapSection";
import { useDirtySnapshot } from "@/hooks/useDirtySnapshot";
import LocationSelector from "@/components/LocationSelector";
import { useLocations } from "@/hooks/useFirestore";
import { useAuth } from "@/contexts/AuthContext";
import { validatePlotIDsFromCSV } from "@/services/firebase";
import { InvalidPlotIDsDialog } from "./InvalidPlotIDsDialog";
import { EmptyColumnsDialog } from "./EmptyColumnsDialog";
import { buildExperimentInput, buildPlotMapData } from "./newExperimentSubmit";

// Re-export PlotMapData for consumers that import from this file
export type { PlotMapData } from "./PlotMapSection";

interface NewExperimentFormProps {
  trialId: string;
  trialName?: string;
  trialLocation?: string;
  onSubmit: (experiment: CreateExperimentInput, plotMapData?: PlotMapData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  submissionProgress?: { progress: number; message: string } | null;
  onPlotMapUploaded?: () => void;
  /** Reports whether the form has unsaved edits, so the parent dialog can guard against accidental close. */
  onDirtyChange?: (dirty: boolean) => void;
}

const MAX_PLOTS_ALLOWED = 35000;

const NewExperimentForm: React.FC<NewExperimentFormProps> = ({
  trialId,
  trialName,
  trialLocation,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submissionProgress = null,
  onPlotMapUploaded,
  onDirtyChange,
}) => {
  const { userRole } = useAuth();
  const { locations, loading: locationsLoading } = useLocations();

  // Only editors and admins can add new locations
  const canEdit = userRole && ["editor", "admin"].includes(userRole);

  // Experiment data state
  const [experimentData, setExperimentData] = useState({
    name: "",
    description: "",
    location: trialLocation || "",
  });

  // Plot map change data from PlotMapSection
  const [plotMapChangeData, setPlotMapChangeData] = useState<PlotMapChangeData | null>(null);

  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Surface unsaved-edit state so the wrapping dialog can confirm before discarding.
  // Dirty when the experiment fields differ from their initial values, or when the
  // user has actually touched the plot map (`hasChanges`, not just its on-mount default).
  const experimentFieldsDirty = useDirtySnapshot(experimentData);
  const isDirty = experimentFieldsDirty || plotMapChangeData?.hasChanges === true;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Invalid plot IDs dialog state
  const [invalidPlotIDsDialog, setInvalidPlotIDsDialog] = useState<{
    open: boolean;
    errors: Array<{ row: number; plotID: string; reason: string }>;
    validCount: number;
    totalCount: number;
  } | null>(null);

  // Empty-metadata-column warning state (drop candidates surfaced on submit)
  const [emptyColumnsDialog, setEmptyColumnsDialog] = useState<{ columns: string[] } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setExperimentData(prev => ({ ...prev, [name]: value }));
  };

  const handlePlotMapChange = useCallback((data: PlotMapChangeData) => {
    setPlotMapChangeData((prevData) => {
      // If plot map just became valid (upload completed), scroll to show it
      if (data.isValid && data.type === "upload" && !prevData?.isValid) {
        onPlotMapUploaded?.();
      }
      return data;
    });
  }, [onPlotMapUploaded]);

  const getTotalPlotCount = (): number => {
    if (!plotMapChangeData) return 0;

    if (plotMapChangeData.type === "generate" && plotMapChangeData.rows && plotMapChangeData.columns) {
      return plotMapChangeData.rows * plotMapChangeData.columns;
    } else if (plotMapChangeData.type === "upload" && plotMapChangeData.csvValidationResult?.parsedData) {
      return plotMapChangeData.csvValidationResult.parsedData.length;
    } else if (plotMapChangeData.type === "sequence" && plotMapChangeData.count) {
      return plotMapChangeData.count;
    }
    return 0;
  };

  const performSubmit = async () => {
    const csvValidationResult = plotMapChangeData?.csvValidationResult;
    const plotIdColumn = plotMapChangeData?.plotIdColumn || "";
    const selectedFile = plotMapChangeData?.file;

    // Pre-validate CSV plot IDs if uploading
    if (plotMapChangeData?.type === "upload" && selectedFile && plotIdColumn) {
      try {
        const validation = await validatePlotIDsFromCSV(selectedFile, {
          plotIDName: plotIdColumn,
          headers: csvValidationResult?.headers || [],
        });

        if (!validation.isValid && validation.errors.length > 0) {
          // Show confirmation dialog
          setInvalidPlotIDsDialog({
            open: true,
            errors: validation.errors,
            validCount: validation.validCount,
            totalCount: validation.totalCount,
          });
          return; // Don't proceed with submission yet
        }
      } catch (error) {
        console.error("Error validating plot IDs:", error);
        // Continue with submission if validation fails - let the backend handle it
      }
    }

    checkEmptyColumnsThenSubmit();
  };

  // Gate: if the upload has entirely-empty metadata columns, let the user drop
  // them before we build the experiment; otherwise submit straight through.
  const checkEmptyColumnsThenSubmit = () => {
    const emptyMetadataColumns = getEmptyMetadataColumns(plotMapChangeData?.csvValidationResult, {
      plotIdColumn: plotMapChangeData?.plotIdColumn,
      rowColumn: plotMapChangeData?.rowColumn,
      colColumn: plotMapChangeData?.colColumn,
    });

    if (emptyMetadataColumns.length > 0) {
      setEmptyColumnsDialog({ columns: emptyMetadataColumns });
      return;
    }

    submitExperiment([]);
  };

  const submitExperiment = (droppedColumns: string[]) => {
    const experiment = buildExperimentInput(
      {
        name: experimentData.name,
        description: experimentData.description,
        location: experimentData.location,
      },
      plotMapChangeData,
      droppedColumns
    );
    onSubmit(experiment, buildPlotMapData(plotMapChangeData));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (!isFormValid()) {
      return;
    }

    await performSubmit();
  };

  const handleInvalidPlotIDsContinue = () => {
    setInvalidPlotIDsDialog(null);
    // Plot IDs already validated and the user confirmed; continue to the
    // empty-columns check before submitting.
    checkEmptyColumnsThenSubmit();
  };

  const handleInvalidPlotIDsCancel = () => {
    setInvalidPlotIDsDialog(null);
  };

  const handleEmptyColumnsResolve = (droppedColumns: string[]) => {
    setEmptyColumnsDialog(null);
    submitExperiment(droppedColumns);
  };

  const handleEmptyColumnsCancel = () => {
    setEmptyColumnsDialog(null);
  };

  const isFormValid = () => {
    const hasRequiredFields = experimentData.name;
    const hasValidPlotMap = plotMapChangeData?.isValid ?? false;
    const isUnderPlotLimit = getTotalPlotCount() <= MAX_PLOTS_ALLOWED;
    return hasRequiredFields && hasValidPlotMap && isUnderPlotLimit;
  };

  const validationErrors = useMemo(() => {
    const errors: { field: string; message: string }[] = [];

    if (!experimentData.name?.trim()) {
      errors.push({ field: "name", message: "Experiment Name is required" });
    }

    if (!plotMapChangeData?.isValid) {
      errors.push({ field: "plotMap", message: "Valid plot map configuration is required" });
    }

    const totalPlots = getTotalPlotCount();
    if (totalPlots > MAX_PLOTS_ALLOWED) {
      errors.push({ field: "plotMap", message: `Maximum ${MAX_PLOTS_ALLOWED.toLocaleString()} plots allowed (you have ${totalPlots.toLocaleString()})` });
    }

    return errors;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experimentData.name, plotMapChangeData]);

  const hasFieldError = (fieldName: string) => {
    return hasSubmitted && validationErrors.some(e => e.field === fieldName);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-1">
        <div className="space-y-4 pt-4">
          {/* Experiment Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className={hasFieldError("name") ? "text-destructive" : ""}>
              Experiment Name <span className={hasFieldError("name") ? "text-destructive" : "text-muted-foreground"}>(required)</span>
            </Label>
            <Input
              id="name"
              name="name"
              value={experimentData.name}
              onChange={handleChange}
              placeholder="Enter experiment name"
              className={hasFieldError("name") ? "border-destructive focus-visible:ring-destructive" : ""}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              value={experimentData.description}
              onChange={handleChange}
              placeholder="Enter a brief description of the experiment"
              rows={3}
            />
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <LocationSelector
              locations={locations}
              locationsLoading={locationsLoading}
              selectedLocation={experimentData.location}
              onSelectLocation={(locationName) => setExperimentData(prev => ({ ...prev, location: locationName }))}
              canAddLocation={canEdit}
              placeholder="Select a location"
            />
          </div>

          {/* Plot Map Section */}
          <PlotMapSection
            mode="create"
            onChange={handlePlotMapChange}
            showFieldError={hasFieldError("plotMap")}
          />
        </div>
      </div>

      {/* Progress indicator when submitting */}
      {isSubmitting && submissionProgress && (
        <div className="pt-4 space-y-3 bg-muted/50 rounded-lg p-4 border">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium truncate">{submissionProgress.message}</span>
                <span className="font-semibold text-primary ml-2">{Math.round(submissionProgress.progress * 100)}%</span>
              </div>
            </div>
          </div>
          <Progress value={submissionProgress.progress * 100} className="h-2" />
        </div>
      )}

      <DialogFooter className="pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" variant="default" disabled={!isFormValid() || isSubmitting}>
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              Creating...
            </span>
          ) : (
            "Create Experiment"
          )}
        </Button>
      </DialogFooter>

      {/* Invalid Plot IDs Confirmation Dialog */}
      {invalidPlotIDsDialog && (
        <InvalidPlotIDsDialog
          open={invalidPlotIDsDialog.open}
          errors={invalidPlotIDsDialog.errors}
          validCount={invalidPlotIDsDialog.validCount}
          totalCount={invalidPlotIDsDialog.totalCount}
          onContinue={handleInvalidPlotIDsContinue}
          onCancel={handleInvalidPlotIDsCancel}
        />
      )}

      {/* Empty Metadata Columns Warning */}
      <EmptyColumnsDialog
        open={emptyColumnsDialog !== null}
        columns={emptyColumnsDialog?.columns ?? []}
        onResolve={handleEmptyColumnsResolve}
        onCancel={handleEmptyColumnsCancel}
      />
    </form>
  );
};

export default NewExperimentForm;
